/**
 * Header bar that sits above the content area only. The brand lives in
 * the sidebar; this bar carries: sidebar toggle (left), centered database
 * switcher, and theme toggle + page action slot on the right.
 */
import {
  IconCheck,
  IconChevronDown,
  IconDatabase,
  IconLayoutSidebar,
  IconMoon,
  IconSun,
} from "@tabler/icons-react";
import {
  useMantineColorScheme,
  useComputedColorScheme,
  Menu,
  Text,
  Tooltip,
} from "@mantine/core";
import { useHeaderActions } from "../lib/header-actions";
import { useActiveDb } from "../lib/active-db";

type Props = {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
};

export function Topbar({ sidebarCollapsed, toggleSidebar }: Props) {
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme("light");
  const actions = useHeaderActions();

  const toggleTheme = () => {
    const next = scheme === "dark" ? "light" : "dark";
    // Briefly add a body class that turns on a global background-color
    // / color / border-color transition so the swap fades instead of
    // popping. Removed after the transition window so hover states etc.
    // keep their own (faster) timings.
    document.body.classList.add("kse-theme-transitioning");
    setColorScheme(next);
    document.documentElement.setAttribute("data-theme", next);
    window.setTimeout(() => {
      document.body.classList.remove("kse-theme-transitioning");
    }, 360);
  };

  return (
    <div className="kse-topbar">
      <div className="kse-topbar__left">
        <Tooltip label={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}>
          <button
            type="button"
            className="kse-icon-btn"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <IconLayoutSidebar size={16} />
          </button>
        </Tooltip>
      </div>

      <div className="kse-topbar__center">
        <DatabaseSwitcher />
      </div>

      {/* Right side: theme toggle + per-route action slot. The
          inline-editing switch lives inside the table view's MRT
          toolbar now, since it's a table-level setting. */}
      <div className="kse-topbar__right">
        <Tooltip label={`Switch to ${scheme === "dark" ? "light" : "dark"} mode`}>
          <button
            type="button"
            className="kse-icon-btn"
            onClick={toggleTheme}
            aria-label="Toggle color scheme"
          >
            {scheme === "dark" ? <IconSun size={15} /> : <IconMoon size={15} />}
          </button>
        </Tooltip>

        {actions ? (
          <>
            <div className="kse-topbar__sep" />
            <div className="kse-topbar__actions">{actions}</div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Database picker — Mantine Menu populated from /api/databases.
 * Active DB shows as a check mark; disabled DBs are dimmed.
 */
function DatabaseSwitcher() {
  const { databases, active, setActiveId, loading } = useActiveDb();

  if (loading && !databases) {
    return <span className="kse-topbar__db">Loading…</span>;
  }
  if (!databases || databases.length === 0) {
    return <span className="kse-topbar__db">No databases</span>;
  }

  const enabledList = databases.filter((d) => d.enabled);
  const disabledList = databases.filter((d) => !d.enabled);

  return (
    <Menu shadow="md" width={280} position="bottom" withArrow>
      <Menu.Target>
        <button
          type="button"
          className="kse-topbar__db-button"
          aria-label="Switch database"
        >
          <IconDatabase size={14} />
          <span className="kse-topbar__db">
            {active?.databaseName ?? "Choose a database"}
          </span>
          <IconChevronDown size={14} />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Databases</Menu.Label>
        {enabledList.map((d) => (
          <Menu.Item
            key={d.id}
            leftSection={
              d.id === active?.id ? (
                <IconCheck size={14} />
              ) : (
                <span style={{ width: 14 }} />
              )
            }
            onClick={() => setActiveId(d.id)}
          >
            <div>
              <Text size="sm" fw={500}>{d.displayName}</Text>
              <Text size="xs" c="dimmed">
                {d.databaseName}
                {d.server !== active?.server ? ` · ${d.server}` : ""}
              </Text>
            </div>
          </Menu.Item>
        ))}
        {disabledList.length > 0 && (
          <>
            <Menu.Divider />
            <Menu.Label>Disabled</Menu.Label>
            {disabledList.map((d) => (
              <Menu.Item key={d.id} disabled>
                <div>
                  <Text size="sm">{d.displayName}</Text>
                  <Text size="xs" c="dimmed">{d.databaseName}</Text>
                </div>
              </Menu.Item>
            ))}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

